import React, { useEffect } from 'react';

import { Grid, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import GenericLoadingText from '@apigrowfinance/pbolt-generic-loading-text';
import Icon from '@apigrowfinance/pbolt-icon';
import { RiCloudOffLine } from 'react-icons/ri';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage } from 'react-intl';

import { StatementItemType } from '@clients/statementsClient';
import { TransactionItem } from '@clients/transactionsClient';
import NoTransactions from '@components/accountStatements/skeletons/NoTransactions';
import TransactionSkeleton from '@components/accountStatements/skeletons/TransactionSkeleton';
import StatementTransaction from '@components/accountStatements/StatementTransaction';
import useGetTransactions from '@hooks/queries/useGetTransactions';
import useLoadMoreIfNecessary from '@hooks/useLoadMoreIfNecessary';
import loadingErrorMessages from '@messages/loadingError';
import getEnumTranslation from '@utils/getEnumTranslation';

export type StatementTransactionsProps = {
    statement: StatementItemType;
    accountId: number;
    fetchNextStatement: () => void;
};

const StatementTransactions = ({
    statement,
    accountId,
    fetchNextStatement,
}: StatementTransactionsProps) => {
    let transactions: TransactionItem[] = [];
    const { data, isError, isLoading, hasNextPage, fetchNextPage } =
        useGetTransactions({
            accountId: accountId,
            statementId: statement?.statement_id,
        });

    useLoadMoreIfNecessary({ fetchNextPage, hasNextPage, isLoading }, [
        data?.pages,
    ]);

    // If we have loaded all the transaction in this statement update the parent so a new statement item can be loaded
    useEffect(() => {
        if (data?.pages[data?.pages?.length - 1]?.has_next === false) {
            fetchNextStatement();
        }
    }, [data?.pages, fetchNextStatement, statement.statement_id]);

    // Create an array combining all the items from all the pages.
    const flattenPages = data?.pages?.flat(1);
    if (flattenPages) {
        transactions = flattenPages.reduce((previousValue, currentValue) => {
            if (currentValue == null || currentValue.items == null) {
                return previousValue;
            }
            return [...previousValue, ...currentValue?.items];
        }, []);
    }

    if (isError) {
        return (
            <ListItem
                sx={{ justifyContent: 'center' }}
                data-testid="loading-error">
                <ListItemAvatar>
                    <Icon
                        avatarProps={{
                            sx: {
                                color: 'text.secondary',
                            },
                        }}>
                        <RiCloudOffLine />
                    </Icon>
                </ListItemAvatar>
                <ListItemText
                    sx={{ flex: 'none' }}
                    primary={getEnumTranslation('TITLE', {
                        messages: loadingErrorMessages,
                    })}
                    secondary={getEnumTranslation('DESCRIPTION', {
                        messages: loadingErrorMessages,
                    })}
                />
            </ListItem>
        );
    }

    return (
        <div data-testid="statement-transactions-component">
            {isLoading ? (
                <TransactionSkeleton />
            ) : transactions?.length ? (
                <Grid item xs={12}>
                    <InfiniteScroll
                        dataLength={transactions?.length}
                        next={fetchNextPage}
                        hasMore={data?.pages[data?.pages?.length - 1]?.has_next}
                        loader={
                            <GenericLoadingText
                                displayText={
                                    <FormattedMessage
                                        id="AccountTransactions.Loading"
                                        defaultMessage="Loading..."
                                    />
                                }
                                showLoader
                            />
                        }>
                        {transactions?.map((transaction: TransactionItem) => {
                            return (
                                <StatementTransaction
                                    key={transaction.id}
                                    accountId={accountId?.toString()}
                                    transaction={transaction}
                                />
                            );
                        })}
                    </InfiniteScroll>
                </Grid>
            ) : (
                <NoTransactions />
            )}
        </div>
    );
};

export default StatementTransactions;
